import {
  ActionName,
  CategoryName,
  DisplayModeName,
  ExecutedActionP,
  IInParamsValues,
} from '@infotech-soe/metaschema';
import createDebug from 'debug';
import compose from 'lodash/fp/compose';
import isEmpty from 'lodash/isEmpty';

import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import { ClientContext } from '../context/IContextService';

import ICategoryInParamsExtractorService from './ICategoryInParamsExtractorService';

const debug = createDebug('err:InParamsExtractor:');

const extract = (results: Record<string, any>): any =>
  compose(
    Object.fromEntries,
    entries =>
      entries.reduce(
        (memo: any[], [name, actionReturn]) =>
          actionReturn.inParam
            ? [...memo, [actionReturn.inParam, (results as any)[name]]]
            : memo,
        []
      ),
    Object.entries
  );

class CategoryInParamsExtractorSingleton implements ICategoryInParamsExtractorService {
  private readonly schemaRepository: ISchemaRepository;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
  }

  public async fromCommand(
    context: ClientContext,
    command: ActionName,
    results: Record<string, any>
  ): Promise<IInParamsValues | undefined> {
    // TODO: group all default command-names
    if (
      !['filter', 'update', 'create', 'delete', 'duplicate'].includes(command)
    ) {
      try {
        const actionSchema = await this.schemaRepository.getAction(
          context.get('Category'),
          command
        );
        const inParams = extract(results)(
          (actionSchema as ExecutedActionP).Returns || ({} as any)
        );
        if (!isEmpty(inParams)) {
          return inParams;
        }
      } catch (e: any) {
        debug(command);
        debug(e.message);
      }
    }
  }

  public async filterByDisplay(
    category: CategoryName,
    displayMode: DisplayModeName,
    inParams: IInParamsValues
  ): Promise<IInParamsValues> {
    const display = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    return compose(
      Object.fromEntries,
      entries =>
        entries.reduce(
          (memo: any[], [name, value]) =>
            display?.inParams?.[name] ? [...memo, [name, value]] : memo,
          []
        ),
      Object.entries
    )(inParams);
  }
}

const CategoryInParamsExtractorService = (() => {
  let instance: ICategoryInParamsExtractorService;
  return new Proxy(CategoryInParamsExtractorSingleton, {
    construct(
      Target: typeof CategoryInParamsExtractorSingleton
    ): ICategoryInParamsExtractorService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CategoryInParamsExtractorService;
